import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Pagination, Row } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";
import { useAuth } from "../context/AuthContext";
import { downloadApiFile } from "../utils/download";

const taskStatuses = ["Pending", "In Progress", "Completed", "On Hold", "Cancelled"];
const PAGE_SIZE = 20;

function getStatusBadgeClass(status) {
  switch (status) {
    case "Completed":
      return "status-badge status-badge-completed";
    case "In Progress":
      return "status-badge status-badge-progress";
    case "On Hold":
      return "status-badge status-badge-hold";
    case "Cancelled":
      return "status-badge status-badge-cancelled";
    case "Pending":
    default:
      return "status-badge status-badge-pending";
  }
}

function formatDaysOverDue(value) {
  if (!value || value <= 0) {
    return "-";
  }

  return `${value} day${value === 1 ? "" : "s"}`;
}

function StaffTasksPage() {
  const history = useHistory();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [filters, setFilters] = useState({
    user_id: "",
    status: ""
  });
  const role = user?.role || "";
  const canFilterByUser = role !== "staff";

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters.user_id, filters.status]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    fetchTasks();
  }, [filters.user_id, filters.status, currentPage]);

  const userNameById = useMemo(
    () =>
      users.reduce((accumulator, user) => {
        accumulator[String(user.id)] = [user.firstname, user.lastname].filter(Boolean).join(" ") || user.username;
        return accumulator;
      }, {}),
    [users]
  );

  const currentUserRecord = useMemo(
    () => users.find((candidate) => candidate.uuid === user?.uuid) || null,
    [user?.uuid, users]
  );

  const currentUserId = String(currentUserRecord?.id || "");

  const visibleUsers = useMemo(() => {
    if (!users.length) {
      return [];
    }

    if (role === "staff") {
      return users.filter((candidate) => String(candidate.id) === currentUserId);
    }

    if (role === "supervisor") {
      return users.filter(
        (candidate) => String(candidate.id) === currentUserId || String(candidate.supervisor_id || "") === currentUserId
      );
    }

    return users;
  }, [currentUserId, role, users]);

  async function fetchTasks() {
    setLoading(true);
    setError("");

    try {
      const params = {
        page: currentPage,
        per_page: PAGE_SIZE
      };

      if (filters.user_id) {
        params.user_id = filters.user_id;
      }
      if (filters.status) {
        params.status = filters.status;
      }

      const response = await api.get("/staff-tasks", { params });
      setTasks(response.data?.items || []);
      setTotalTasks(response.data?.total || 0);
      setTotalPages(response.data?.total_pages || 0);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load staff tasks.");
      setTasks([]);
      setTotalTasks(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }

  async function fetchUsers() {
    try {
      const response = await api.get("/users");
      setUsers(response.data || []);
    } catch (err) {
      // Keep page usable if user lookup fails.
    }
  }

  function handleFilterChange(event) {
    const { name, value } = event.target;
    setFilters((current) => ({
      ...current,
      [name]: value
    }));
  }

  function handleClearFilters() {
    setCurrentPage(1);
    setFilters({
      user_id: "",
      status: ""
    });
  }

  async function handleDownload() {
    setDownloading(true);

    try {
      await downloadApiFile("/staff-tasks/export", {
        params: {
          user_id: filters.user_id || undefined,
          status: filters.status || undefined
        },
        fallbackFileName: "staff-tasks.csv"
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to download staff tasks.");
    } finally {
      setDownloading(false);
    }
  }

  function renderPaginationControls(position) {
    if (totalPages <= 1) {
      return (
        <div className={`activity-pagination-bar ${position}`}>
          <div className="activity-total-count">Total staff tasks: {totalTasks}</div>
        </div>
      );
    }

    return (
      <div className={`activity-pagination-bar ${position}`}>
        <div className="activity-total-count">
          Total staff tasks: {totalTasks}
          <span className="text-muted ms-2">
            Showing {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, totalTasks)}
          </span>
        </div>
        <Pagination className="mb-0 app-pagination">
          <Pagination.Prev onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage === 1}>
            Previous
          </Pagination.Prev>
          <Pagination.Item active disabled className="app-pagination-status">
            Page {currentPage} of {totalPages}
          </Pagination.Item>
          <Pagination.Next
            onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Pagination.Next>
        </Pagination>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Staff Tasks"
        action={
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={() => history.push("/staff-tasks/new")}>
              Add staff task
            </Button>
            <Button variant="outline-primary" onClick={handleDownload} disabled={downloading || loading} title="Download staff tasks">
              {downloading ? "Downloading..." : "Download"}
            </Button>
            <Button variant="outline-secondary" onClick={handleClearFilters}>
              Clear filters
            </Button>
          </div>
        }
      />

      <Row className="g-4">
        <Col xs={12}>
          <div className="content-card table-card">
            <h5 className="mb-3">Task list</h5>
            <Row className="g-3 mb-3">
              <Col md={6} xl={3}>
                <label className="form-label">Assigned To</label>
                {canFilterByUser ? (
                  <Form.Select name="user_id" value={filters.user_id} onChange={handleFilterChange}>
                    <option value="">All visible users</option>
                    {visibleUsers.map((staffUser) => (
                      <option key={staffUser.id} value={staffUser.id}>
                        {[staffUser.firstname, staffUser.lastname].filter(Boolean).join(" ") || staffUser.username}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    value={
                      currentUserRecord
                        ? [currentUserRecord.firstname, currentUserRecord.lastname].filter(Boolean).join(" ") || currentUserRecord.username
                        : "My tasks"
                    }
                    readOnly
                  />
                )}
              </Col>
              <Col md={6} xl={3}>
                <label className="form-label">Status</label>
                <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
                  <option value="">All statuses</option>
                  {taskStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            {loading ? <LoadingState label="Loading staff tasks..." /> : null}
            {error ? <Alert variant="danger">{error}</Alert> : null}
            {!loading && !error && !tasks.length ? (
              <EmptyState title="No staff tasks yet" text="The first task will appear here after you create it." />
            ) : null}

            {!loading && tasks.length ? (
              <>
                {renderPaginationControls("top")}
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th className="staff-task-id-cell">Task ID</th>
                        <th className="staff-task-text-cell">Task</th>
                        <th className="staff-task-user-cell">Assigned To</th>
                        <th className="staff-task-user-cell">Assigned By</th>
                        <th>Status</th>
                        <th className="staff-task-date-cell">Days overdue</th>
                        <th className="staff-task-date-cell">Date assigned</th>
                        <th className="staff-task-date-cell">Due date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task) => (
                        <tr key={task.uuid}>
                          <td className="staff-task-id-cell">
                            <span className="badge-soft activity-code-badge">#{task.id}</span>
                          </td>
                          <td className="staff-task-text-cell">
                            <button
                              type="button"
                              className="staff-task-link"
                              onClick={() => history.push(`/staff-tasks/${task.uuid}`)}
                            >
                              {task.task_given}
                            </button>
                          </td>
                          <td className="staff-task-user-cell">{userNameById[String(task.user_id)] || task.user_id}</td>
                          <td className="staff-task-user-cell">
                            {userNameById[String(task.assigned_by_id)] || task.assigned_by_id || "-"}
                          </td>
                          <td>
                            <span className={getStatusBadgeClass(task.status)}>{task.status}</span>
                          </td>
                          <td className="staff-task-date-cell">{formatDaysOverDue(task.days_over_due)}</td>
                          <td className="staff-task-date-cell">{task.date_assigned || "-"}</td>
                          <td className="staff-task-date-cell">{task.date_due || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {renderPaginationControls("bottom")}
              </>
            ) : null}
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default StaffTasksPage;
